import { addGroceryItemRequestSchema, checkOffRequestSchema, checkOffResponseSchema, checkoffDefaultsQuerySchema, checkoffDefaultsResponseSchema, createGroceryListRequestSchema, groceryItemDtoSchema, groceryListDetailSchema, groceryListDtoSchema, groceryListQuerySchema, groceryListsResponseSchema, haveItemRequestSchema, haveItemResponseSchema, receiptApplyRequestSchema, receiptApplyResponseSchema, receiptScanResponseSchema, regenerateGroceryListRequestSchema, uncheckItemRequestSchema, updateGroceryItemRequestSchema, updateGroceryListRequestSchema, } from '@fewerbags/schema';
const detail = { parse: groceryListDetailSchema };
const list = { parse: groceryListDtoSchema };
const item = { parse: groceryItemDtoSchema };
/**
 * /api/v1/groceries — grocery lists generated from plans, manual/restock items,
 * check-off with learned expiration defaults (PLAN §4.4), and live sync over
 * Server-Sent Events (D14).
 */
export function createGroceriesApi(http, base) {
    const id = (listId) => `${base}/${encodeURIComponent(listId)}`;
    const itemId = (listId, groceryItemId) => `${id(listId)}/items/${encodeURIComponent(groceryItemId)}`;
    return {
        /** This household's grocery lists, newest first. */
        list: async (query = {}) => {
            const q = groceryListQuerySchema.parse(query);
            return http.get(base, {
                query: { status: q.status, limit: q.limit },
                parse: groceryListsResponseSchema,
            });
        },
        /** Generate from plans in [dateFrom, dateTo]; omit both for a blank list. */
        create: async (input) => http.post(base, createGroceryListRequestSchema.parse(input), detail),
        get: async (listId) => http.get(id(listId), detail),
        /** Rename, change store, or change status (open/shopping/done/archived). */
        update: async (listId, input) => http.patch(id(listId), updateGroceryListRequestSchema.parse(input), list),
        remove: async (listId) => http.del(id(listId)),
        /** Re-run generation for this list; manual items and existing check-offs are kept. */
        regenerate: async (listId, input) => http.post(`${id(listId)}/regenerate`, regenerateGroceryListRequestSchema.parse(input), detail),
        items: {
            /** ingredientId or name (exactly one). */
            add: async (listId, input) => http.post(`${id(listId)}/items`, addGroceryItemRequestSchema.parse(input), item),
            update: async (listId, groceryItemId, input) => http.patch(itemId(listId, groceryItemId), updateGroceryItemRequestSchema.parse(input), item),
            remove: async (listId, groceryItemId) => http.del(itemId(listId, groceryItemId)),
            /** Prefill for the check-off sheet: learned expiration, storage location, price, location. */
            checkoffDefaults: async (listId, groceryItemId, query = {}) => {
                const q = checkoffDefaultsQuerySchema.parse(query);
                return http.get(`${itemId(listId, groceryItemId)}/checkoff-defaults`, {
                    query: { productId: q.productId, qty: q.qty, unit: q.unit },
                    parse: checkoffDefaultsResponseSchema,
                });
            },
            /** Records the purchase + pantry item (and any price/location observation). */
            check: async (listId, groceryItemId, input) => http.post(`${itemId(listId, groceryItemId)}/check`, checkOffRequestSchema.parse(input), {
                parse: checkOffResponseSchema,
            }),
            /** "I have this" (D35): resolve the row from home — optional pantry item, no purchase. */
            have: async (listId, groceryItemId, input = {}) => http.post(`${itemId(listId, groceryItemId)}/have`, haveItemRequestSchema.parse(input), {
                parse: haveItemResponseSchema,
            }),
            /** Undo a check-off or a "have". Pass the `purchaseId` the shopper was looking at: if the item has been
             *  checked off again since, the server answers 409 `CHECK_CHANGED` and leaves it checked. */
            uncheck: async (listId, groceryItemId, expectedPurchaseId) => http.post(`${itemId(listId, groceryItemId)}/uncheck`, expectedPurchaseId ? uncheckItemRequestSchema.parse({ expectedPurchaseId }) : undefined, item),
        },
        /**
         * Receipt scanning (D34). `scan` sends a photo of the receipt (the server
         * reads it in memory and never stores it) and returns a proposal matching
         * receipt lines to the list's unchecked rows; `apply` checks off the rows
         * the person confirmed, with the receipt's prices. Nothing is kept between
         * the two calls. 503 `RECEIPT_SCAN_UNAVAILABLE` when the server has no AI key.
         */
        receipt: {
            scan: async (listId, file) => {
                const form = new FormData();
                form.append('file', file, file instanceof File ? file.name : 'receipt.jpg');
                return http.postForm(`${id(listId)}/receipt`, form, { parse: receiptScanResponseSchema });
            },
            apply: async (listId, input) => http.post(`${id(listId)}/receipt/apply`, receiptApplyRequestSchema.parse(input), {
                parse: receiptApplyResponseSchema,
            }),
        },
        /**
         * Same-origin path for the list's live-sync SSE stream — open with
         * `new EventSource(url, { withCredentials: true })` and listen for every name in
         * `groceryEventTypes` (frames are named events); each `data`
         * is JSON matching `groceryEventSchema`.
         */
        eventsUrl: (listId) => `${id(listId)}/events`,
    };
}
//# sourceMappingURL=groceries.js.map